/**
 * Field-level normalizers for the canonical vehicle telemetry store
 * (spec `vehicle-realtime-data-consistency`, design "Normalization").
 *
 * The tracking page reads movement from three sources with different shapes:
 *   - `GET /formatted/latest/vehicles` -> a per-field aggregate (`{ t, v }` wrappers)
 *     where each field can come from a different packet,
 *   - `GET /formatted/vehicles/{plate}` -> chronological packets,
 *   - SignalR -> single live packets.
 *
 * Normalizing them here keeps one rule in one place: movement freshness comes from
 * the speed observation itself (`device_data.velocity.t`, or the `source_time` of a
 * speed-bearing packet) and never from generic packet activity. `latestPacketAt` is
 * connectivity diagnostics only and must never be used as a fallback for
 * `movement.observedAt`.
 */

export const LATEST_VEHICLES_SOURCE = "latest-vehicles-api";
export const ROUTE_SOURCE = "route-api";
export const REALTIME_SOURCE = "signalr";

const DEVICE_DATA_PACKET_TYPE = "device_data";

/** Sections of a latest-vehicles item that can carry plate/source identity. */
const IDENTITY_SECTIONS = ["device_data", "heartbeat", "device_reply", "login", "registration"];

/**
 * Unwraps a `{ t, v }` field wrapper used by the latest-vehicles aggregate.
 * Plain values (route/SignalR packets) are returned as-is.
 */
export const unwrapValue = (field) =>
  field && typeof field === "object" && "v" in field ? field.v : field;

/**
 * Reads the observation time of an aggregated field (`field.t`).
 * @returns {number|null} milliseconds, or `null` when absent/invalid.
 */
export const getFieldTimestamp = (field) => {
  const timestamp = Number(field?.t);
  return Number.isFinite(timestamp) ? timestamp : null;
};

/**
 * Zero is a valid speed. Only `null`, `undefined`, `""` and non-numeric values
 * count as missing speed data.
 */
export const hasValidSpeed = (speed) =>
  speed !== null && speed !== undefined && speed !== "" && Number.isFinite(Number(speed));

/**
 * Parses a packet timestamp. Accepts epoch milliseconds (number or numeric
 * string) and ISO date strings.
 * @returns {number|null} milliseconds, or `null` when unparseable.
 */
export const parseTimestamp = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return numericValue;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstFiniteTimestamp = (...values) => {
  for (const value of values) {
    const timestamp = parseTimestamp(value);
    if (timestamp !== null) return timestamp;
  }
  return null;
};

const isPresent = (value) => value !== null && value !== undefined && value !== "";

const readIdentity = (item, key) => {
  for (const section of IDENTITY_SECTIONS) {
    const value = unwrapValue(item?.[section]?.[key]);
    if (isPresent(value)) return value;
  }
  return null;
};

/**
 * A packet may only create or refresh a movement observation when it is a
 * `device_data` packet. Login/heartbeat/registration packets carry generic
 * activity only. A packet without a declared type is treated as telemetry
 * because the route endpoint does not always label rows.
 */
const canCarryTelemetry = (packetType) =>
  !isPresent(packetType) || packetType === DEVICE_DATA_PACKET_TYPE;

/**
 * Normalizes one item of the `GET /formatted/latest/vehicles` response into the
 * canonical per-field current entry.
 *
 * - `device_data.velocity.v.speed` -> `movement.speed`
 * - `device_data.velocity.t`       -> `movement.observedAt`
 * - `latest_packet.source_time` / generic `device_data.source_time` -> `latestPacketAt`
 *   (diagnostics only)
 *
 * When `device_data.velocity.t` is missing, `movement.observedAt` stays `null`;
 * the generic `source_time` is never substituted, so freshness is reported as
 * unknown and movement status degrades safely. A speed value without a timestamp
 * is still retained as an unknown-freshness fallback.
 *
 * @param {Object} item - Raw latest-vehicles item.
 * @returns {Object|null} Canonical current entry, or `null` for an unusable item.
 */
export const normalizeLatestVehicle = (item) => {
  if (!item || typeof item !== "object") return null;

  const plateNumber = readIdentity(item, "plate_number");
  if (!isPresent(plateNumber)) return null;

  const deviceData = item.device_data ?? {};
  const velocityField = deviceData.velocity;
  const velocity = unwrapValue(velocityField) ?? {};
  const ignitionField = deviceData.ignition;
  const ignitionValue = unwrapValue(ignitionField);
  const positionField = deviceData.gps;
  const gps = unwrapValue(positionField);
  const positionValue = gps?.position ?? gps ?? null;

  const speed = velocity?.speed;

  return {
    plateNumber,
    sourceId: readIdentity(item, "source_id"),
    // Diagnostics only. Never a fallback for movement.observedAt.
    latestPacketAt: firstFiniteTimestamp(
      item?.latest_packet?.source_time,
      unwrapValue(deviceData.source_time),
      getFieldTimestamp(deviceData.source_time),
      unwrapValue(deviceData.server_time),
      getFieldTimestamp(deviceData.server_time)
    ),
    latestPacketType: item?.latest_packet?.packet_type ?? null,
    movement: hasValidSpeed(speed)
      ? {
          speed: Number(speed),
          direction: isPresent(velocity?.direction) ? Number(velocity.direction) : null,
          observedAt: getFieldTimestamp(velocityField),
          // The latest API does not expose which packet supplied the speed, so the
          // generic latest_packet.packet_id must not be attached here.
          packetId: null,
          source: LATEST_VEHICLES_SOURCE,
        }
      : null,
    ignition: isPresent(ignitionValue)
      ? {
          value: ignitionValue,
          observedAt: getFieldTimestamp(ignitionField),
          source: LATEST_VEHICLES_SOURCE,
        }
      : null,
    position: positionValue
      ? {
          value: positionValue,
          observedAt: getFieldTimestamp(positionField),
          source: LATEST_VEHICLES_SOURCE,
        }
      : null,
  };
};

/**
 * Normalizes a route or SignalR packet.
 *
 * The raw packet fields are preserved (the live table renders real packets and
 * dedups on `packet_id` / `plate_number + source_id + source_time + packet_type`)
 * and canonical fields are added alongside:
 *
 * - `movementObservedAt` is the packet `source_time`, but only when the packet
 *   carries a valid speed; otherwise `null`.
 * - `fields.movement` / `fields.ignition` / `fields.position` are the per-field
 *   observations the reducer merges by timestamp.
 * - `latestPacketAt` is generic activity, set for any packet type.
 *
 * @param {Object} packet - Raw route/SignalR packet.
 * @param {string} [source] - Origin of the packet (route/SignalR).
 * @returns {Object|null} Normalized packet, or `null` for an unusable packet.
 */
export const normalizePacket = (packet, source = ROUTE_SOURCE) => {
  if (!packet || typeof packet !== "object") return null;

  const plateNumber = unwrapValue(packet.plate_number);
  if (!isPresent(plateNumber)) return null;

  const packetType = packet.packet_type ?? null;
  const sourceTime = parseTimestamp(unwrapValue(packet.source_time));
  const serverTime = parseTimestamp(unwrapValue(packet.server_time));
  const telemetryPacket = canCarryTelemetry(packetType);

  const velocity = unwrapValue(packet.velocity) ?? {};
  const speed = velocity?.speed;
  // Only a speed-bearing telemetry packet observes movement. A login/heartbeat
  // packet, or a device_data packet without velocity, leaves movement untouched.
  const movementObservedAt =
    telemetryPacket && hasValidSpeed(speed) && sourceTime !== null ? sourceTime : null;

  const ignitionValue = unwrapValue(packet.ignition);
  const gps = unwrapValue(packet.gps);
  const positionValue = gps?.position ?? null;

  return {
    ...packet,
    plateNumber,
    sourceId: unwrapValue(packet.source_id) ?? null,
    packetId: unwrapValue(packet.packet_id) ?? null,
    packetType,
    sourceTime,
    serverTime,
    source,
    // Diagnostics only, for any packet type.
    latestPacketAt: sourceTime,
    movementObservedAt,
    fields: {
      movement:
        telemetryPacket && hasValidSpeed(speed)
          ? {
              speed: Number(speed),
              direction: isPresent(velocity?.direction) ? Number(velocity.direction) : null,
              observedAt: movementObservedAt,
              packetId: unwrapValue(packet.packet_id) ?? null,
              source,
            }
          : null,
      ignition:
        telemetryPacket && isPresent(ignitionValue)
          ? { value: ignitionValue, observedAt: sourceTime, source }
          : null,
      position:
        telemetryPacket && positionValue
          ? { value: positionValue, observedAt: sourceTime, source }
          : null,
    },
  };
};

/**
 * Dedup key for normalized or raw packets: `packetId` when available, otherwise
 * `plateNumber + sourceId + sourceTime + packetType`.
 */
export const getPacketDedupKey = (packet) => {
  const packetId = packet?.packetId ?? unwrapValue(packet?.packet_id);
  if (isPresent(packetId)) return String(packetId);

  return [
    packet?.plateNumber ?? unwrapValue(packet?.plate_number) ?? "",
    packet?.sourceId ?? unwrapValue(packet?.source_id) ?? "",
    packet?.sourceTime ?? unwrapValue(packet?.source_time) ?? "",
    packet?.packetType ?? packet?.packet_type ?? "",
  ].join("|");
};
